import { useEffect, useState } from "react";
import { ApiError } from "./client.js";

export type Resource<T> =
  | { state: "loading" }
  | { state: "error"; message: string; code: string | null }
  | { state: "ready"; value: T };

export function describeResourceError(
  error: unknown,
): { message: string; code: string | null } {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return {
        message: "サインインの有効期限が切れました。画面を再読み込みしてください。",
        code: error.code,
      };
    }
    if (error.status === 403) {
      return {
        message: "この操作を行う権限がありません。管理者に確認してください。",
        code: error.code,
      };
    }
    return { message: error.message, code: error.code };
  }
  if (error instanceof Error) return { message: error.message, code: null };
  return { message: "読み込みに失敗しました。", code: null };
}

/** 取得中・失敗・成功を画面が必ず区別できるようにする。 */
export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
): Resource<T> {
  const [resource, setResource] = useState<Resource<T>>({ state: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    setResource({ state: "loading" });
    load(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setResource({ state: "ready", value });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const { message, code } = describeResourceError(error);
        setResource({ state: "error", message, code });
      });
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return resource;
}
